/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 텍스트 편집 엘리먼트를 정의한다.
 *
 * @file /src/rootin/elements/text/Text.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 9. 7.
 */
import * as parent from '@rootin/base/Element';
import { Block } from '@rootin/base/Block';
import { Html } from '@utils/Html';
import { LanguageText, Language } from '@utils/Language';
import './Text.css';

export interface Properties extends parent.Properties {
    /**
     * CSS/구조 마커용 역할
     */
    role?: string;

    /**
     * 내용이 비어있을 때 표시할 placeholder 텍스트
     */
    placeholder?: LanguageText;
}

export class Text extends parent.Element {
    /**
     * 텍스트를 탐색할 HTML 태그
     */
    static readonly genericWrapperTags: string[] = ['DIV', "rootin-element[data-type='text']"];

    /**
     * 엘리먼트타입
     */
    type: string = 'text';

    /**
     * 엘리먼트종류
     */
    kind: 'text' | 'atomic' | 'container' = 'text';

    /**
     * IME 조합(한글 등) 진행 중 여부
     */
    #composing: boolean = false;

    /**
     * expectEdit() 이후 이 엘리먼트 자신의 input 리스너가 실제로 탔는지 확인하기 위한 플래그.
     */
    #synced: boolean = true;

    /**
     * 텍스트 편집 엘리먼트를 정의한다.
     *
     * @param block - 이 엘리먼트가 속한 블록
     * @param properties - 엘리먼트설정
     */
    constructor(
        block: Block,
        public properties: Properties
    ) {
        super(block, properties);
    }

    /**
     * 현재 캐럿 오프셋을 가져온다.
     *
     * @returns 캐럿 오프셋
     */
    captureCaret(): { start: number; end: number } | null {
        return this.getEditor().selection.getOffsets(this);
    }

    /**
     * 캐럿 오프셋을 복원한다.
     *
     * @param state - 복원할 캐럿 오프셋
     */
    restoreCaret(state: { start: number; end: number } | null): void {
        if (state === null) {
            return;
        }
        this.getEditor().selection.setOffsets(this, state.start, state.end);
    }

    /**
     * 엘리먼트를 렌더링한다.
     */
    async render(): Promise<void> {
        const $el = this.$getElement();
        $el.setAttr('data-role', this.properties.role ?? 'text');

        if (this.properties.placeholder !== undefined) {
            const text = await Language.resolveText(this.properties.placeholder);
            $el.setAttr('data-placeholder', text);
        }

        $el.on('compositionstart', () => {
            this.#composing = true;
        });

        $el.on('beforeinput', () => {
            this.onBeforeEdit();
        });

        $el.on('compositionend', () => {
            this.#synced = true;
            this.#syncEmpty();
            this.#composing = false;
            this.onEdit();
        });

        $el.on('input', () => {
            this.#synced = true;
            this.#syncEmpty();
            if (this.#composing === true) {
                return;
            }
            this.onEdit();
        });

        this.#syncEmpty();
    }

    /**
     * 엘리먼트가 비었는지 확인한다.
     *
     * @returns 엘리먼트가 비었는지 여부
     */
    isEmpty(): boolean {
        return (this.$getElement().toHtml(true) ?? '').replaceAll('<br>', '').trim() === '';
    }

    /**
     * 엘리먼트값을 가져온다.
     * @todo HTML 마크업을 JSON 으로 직렬화한다.
     *
     * @returns 엘리먼트값
     */
    getValue(): any {
        return this.$getElement().toHtml(true);
    }

    /**
     * 엘리먼트값을 지정한다.
     * @todo 직렬화된 마크업을 HTML 로 복원한다.
     *
     * @param value - 엘리먼트값
     */
    async setValue(data: any): Promise<void> {
        this.$getElement().html((data ?? '') as string);
        this.#syncEmpty();
    }

    /**
     * 엘리먼트가 포커스를 잃었을 때 이벤트를 처리한다.
     */
    onBlur(): void {
        this.#syncEmpty();
    }

    /**
     * 클립보드에서 붙여넣기 하였을 때 이벤트를 처리한다.
     *
     * @param type - 클립보드 타입
     * @param data - 클립보드 데이터
     * @param wrappers - 항상 투명하게 펼쳐볼 구조적 wrapper 태그/셀렉터 목록
     * @param extractTags - 내가 직접 소유하는(찾아서 그 하나만 추출할) 태그 목록
     * @param extractOnly - true 면 문서 어디에 있든 extractTags 중 첫 occurrence 하나만
     * 찾아 그 태그의 선두 텍스트만 뽑고, 그 외(wrapper 를 펼치다 우연히 만난 일반 텍스트/
     * 서식 등)는 절대 주워담지 않는다 — 그런 건 내(예: 제목) 것이 아니라 다른 블록타입이
     * 이어받아야 할 몫이기 때문이다(false 면 기존처럼 wrapper 를 펼치며 만나는 인라인
     * 내용도 함께 모은다 — 예: 포커스를 가진 나 자신에게 캐럿 삽입할 때는 이게 맞다).
     * @returns `remain`(처리결과, true 면 모든 데이터를 처리함, 그렇지 않으면 처리되지 않은
     * 데이터)과 `matched`(extractTags 중 실제로 매칭돼 추출한 태그명, 없으면 null)
     */
    async onPaste(
        type: string,
        data: any,
        wrappers: string[],
        extractTags: string[] = [],
        extractOnly: boolean = false
    ): Promise<{ remain: true | string; matched: string | null }> {
        // 타입이 text/* 가 아닌 경우 바로 return data;
        if (type.startsWith('text/') === false) {
            return { remain: data, matched: null };
        }

        let html = '';
        let remain: true | string = data;
        let matched: string | null = null;

        if (type == 'text/plain') {
            // 순수 텍스트는 HTML 로 취급해 그대로 삽입할 것이므로, <, >, & 등을 이스케이프하고
            // 줄바꿈은 <br> 로 바꿔둔다(안 그러면 "5 < 10" 같은 텍스트가 태그로 오인돼 사라진다).
            html = Html.escape(data as string).replace(/\r\n|\r|\n/g, '<br>');
            remain = true;
        } else {
            // DOM 을 다룰 수 있게 data 를 DOM 으로 처리.
            const dom = document.createElement('div');
            dom.innerHTML = data as string;

            // el 자신이 extractTags 태그일 때, el 의 직속 자식을 문서 순서대로 보며 텍스트
            // 노드만 이어붙이다가 첫 자식 엘리먼트를 만나면 멈춘다(그 이상은 다음 붙여넣기
            // 시도에게 넘긴다 — el 이 아직 처리 못한 내용을 담은 채 그대로 남아야 하므로 el
            // 자체는 여기서 건드리지 않는다, 소비할 텍스트노드만 호출한 쪽이 옮긴다).
            //
            // el 이 텍스트 하나 없이 곧장 또 다른 extractTags 태그를 자식으로 가지면(예:
            // `<h1><h2>제목</h2></h1>`), el 자신은 아무 것도 기여하지 못하는 빈 wrapper 이므로
            // 건너뛰고 그 자식을 대상으로 재귀적으로 다시 찾는다 — 그래서 matched 로 돌아오는
            // 태그명은 항상 실제로 텍스트를 내어준 가장 안쪽 태그다(레벨 등 태그 자체의
            // 정체성이 중요한 호출자가 정확한 태그를 알 수 있게 한다).
            const extractLeading = (el: globalThis.Element): { el: globalThis.Element; nodes: ChildNode[] } | null => {
                const leading: ChildNode[] = [];
                for (const child of [...el.childNodes]) {
                    if (child.nodeType === Node.COMMENT_NODE) {
                        child.remove();
                        continue;
                    }
                    if (child.nodeType === Node.TEXT_NODE) {
                        leading.push(child);
                        continue;
                    }
                    if (child.nodeType === Node.ELEMENT_NODE) {
                        const childEl = child as globalThis.Element;
                        if (leading.length === 0 && extractTags.includes(childEl.tagName)) {
                            return extractLeading(childEl);
                        }
                    }
                    break;
                }
                return leading.length > 0 ? { el, nodes: leading } : null;
            };

            if (extractOnly === true) {
                // wrapper 를 펼치고 인라인을 주워담는 일반 로직을 아예 타지 않는다 — 구조가
                // 무엇이든 상관없이(재귀적으로 아무 엘리먼트든 들여다봐도 안전하다, 여기서는
                // 아무 것도 옮기거나 지우지 않으니까) extractTags 중 문서상 첫 번째로 나오는
                // occurrence 만 찾는다. 예를 들어 원본이 "작성자 아무개 <h2>제목</h2>" 라면
                // "작성자 아무개"는 그 어떤 wrapper 로도 인식하지 않고 그냥 지나쳐, h2 만
                // 찾아내 뽑는다 — "작성자 아무개"는 손대지 않고 그대로 남겨 다른 블록타입
                // (본문 등)이 이어받게 한다.
                const findFirst = (node: globalThis.Element): globalThis.Element | null => {
                    for (const child of Array.from(node.children)) {
                        if (extractTags.includes(child.tagName)) {
                            return child;
                        }
                        const found = findFirst(child);
                        if (found !== null) {
                            return found;
                        }
                    }
                    return null;
                };

                const target = findFirst(dom);
                const found = target !== null ? extractLeading(target) : null;

                if (found !== null) {
                    matched = found.el.tagName;
                    const buffer = document.createElement('div');
                    for (const node of found.nodes) {
                        buffer.appendChild(node);
                    }
                    if (found.el.childNodes.length === 0) {
                        found.el.remove();
                    }
                    html = buffer.innerHTML;
                    remain = dom.innerHTML === '' ? true : dom.innerHTML;
                }

                if (html !== '') {
                    if (this.getEditor().focus.getElement() === this) {
                        this.#insertAtCaret(html);
                    } else {
                        await this.setValue(html);
                    }
                }

                return { remain, matched };
            }

            // 최종적으로 실제 넣을 내용만 모아둘 단 하나의 버퍼.
            const buffer = document.createElement('div');

            // wrappers 에 나열된 태그(P, DIV 등)나 `태그명[data-type='값']`(예:
            // `rootin-block[data-type='paragraph']`, `rootin-element[data-type='text']`)
            // 형태의 항목과 일치하는지로 "구조적 wrapper"인지 판단한다.
            const isWrapper = (el: globalThis.Element): boolean =>
                wrappers.some((wrapper) => {
                    const match = wrapper.match(/^([a-z-]+)\[data-type='([^']*)'\]$/i);
                    if (match !== null) {
                        return el.tagName === match[1].toUpperCase() && el.getAttribute('data-type') === match[2];
                    }
                    return el.tagName === wrapper;
                });

            // node 의 자식(childNodes — 텍스트노드 포함)을 문서 순서대로 훑어 buffer 로 옮긴다.
            //
            // 텍스트노드는 그대로 옮긴다. extractTags 에 있는 태그를 만나면 그 태그 하나만큼만
            // (extractLeading 참고) 추출하고 전체 순회를 즉시 멈춘다 — occurrence 하나 = 블록
            // 하나이므로, 더 있어도 이번엔 여기까지만 처리하고 나머지는 다음 붙여넣기 시도(다시
            // 이 함수가 호출되거나, 다른 블록타입이 이어받는다)에 맡긴다. 구조적 wrapper(P/DIV
            // 등)를 만나면 그 안까지 재귀적으로 같은 buffer 에 이어서 판다 — 그 wrapper 안의
            // 내용을 끝까지(막히지 않고) 다 옮겼다면, 그건 인라인이 아닌 태그 하나를 벗긴 것과
            // 같으므로 그 경계에 <br> 하나를 끼워넣는다(필요없는 자리에 낀 건 마지막에 한 번에
            // 정리한다). 재귀 도중 막히면(blocked) 그 wrapper 는 아직 처리 못한 내용이
            // 남아있는 것이므로 지우지 않고 <br> 도 끼워넣지 않는다.
            //
            // 그 외의 요소는 인라인인지만 확인한다 — 인라인이면 더 이상 재귀적으로 들어가지
            // 않고 통째로 buffer 로 옮긴다(안에 뭐가 있든 Html.stripHtml 이 마지막에 한 번에
            // 정리해줄 것이다). 인라인이 아니면(wrappers 에도 없고 extractTags 도 아니고
            // 인라인도 아닌 태그, 또는 타입이 다른 rootin-block) "내가 처리할 내용이 아니라
            // 다른 블록타입이 이어받아야 할 내용"이므로 그 지점에서 멈춘다(blocked).
            const walk = (node: globalThis.Element): boolean => {
                for (const child of [...node.childNodes]) {
                    if (child.nodeType === Node.COMMENT_NODE) {
                        child.remove();
                        continue;
                    }

                    if (child.nodeType === Node.TEXT_NODE) {
                        buffer.appendChild(child);
                        continue;
                    }

                    if (child.nodeType !== Node.ELEMENT_NODE) {
                        continue;
                    }

                    const el = child as globalThis.Element;

                    if (extractTags.includes(el.tagName) === true) {
                        const found = extractLeading(el);
                        if (found !== null) {
                            matched = found.el.tagName;
                            for (const node of found.nodes) {
                                buffer.appendChild(node);
                            }
                            // found.el 이 나(el)가 아니라 그 안쪽의(비어있던 wrapper 를 건너뛰고
                            // 찾아낸) 더 깊은 태그일 수 있다 — 그 태그가 이제 텅 비었으면(자기
                            // 몫을 전부 내줬으면) 지워서, 다음 시도가 이 빈 껍데기에 다시
                            // 걸려(아무 것도 못 찾고 blocked) 뒤에 남은 형제(예: 다음 제목)를
                            // 영영 못 보는 일이 없게 한다.
                            if (found.el.childNodes.length === 0) {
                                found.el.remove();
                            }
                        }
                        return true;
                    }

                    if (isWrapper(el) === true) {
                        // <br> 을 어디에 끼워넣을지 표시해두려고, el 의 내용이 buffer 로
                        // 옮겨지기 직전 시점의 마지막 노드를 기억해둔다(el 이 buffer 맨
                        // 앞부터 채우면 null).
                        const before = buffer.lastChild;
                        if (walk(el) === true) {
                            return true;
                        }
                        if (Html.isInlineTag(el.tagName) === false) {
                            // 블록요소는 브라우저 기본 렌더링상 자기 앞뒤로 항상 줄바꿈을
                            // 만든다 — 앞쪽 경계(el 의 내용이 시작되기 직전)뿐 아니라 뒤쪽
                            // 경계(el 의 내용이 끝난 직후, 바로 이어지는 형제와의 사이)에도
                            // 넣어야, 예를 들어 `<h1>안녕</h1>그 뒤 텍스트` 가 "안녕그 뒤
                            // 텍스트"로 붙어버리지 않고 제대로 분리된다. 이어지는 다른
                            // 블록요소가 자기 앞쪽에 또 넣는 것과 겹쳐 <br><br> 이 될 수 있지만
                            // 마지막에 연속된 <br> 은 하나로 합친다.
                            const before_br = document.createElement('br');
                            if (before === null) {
                                buffer.insertBefore(before_br, buffer.firstChild);
                            } else {
                                before.after(before_br);
                            }
                            buffer.appendChild(document.createElement('br'));
                        }
                        el.remove();
                        continue;
                    }

                    if (el.tagName === 'ROOTIN-BLOCK' || Html.isInlineTag(el.tagName) === false) {
                        return true;
                    }

                    buffer.appendChild(el);
                }

                return false;
            };

            walk(dom);

            // 실제로 하나라도 소비했는지(buffer 에 옮겨진 게 있는지) — 아무것도 못 건졌으면
            // dom.innerHTML 로 재직렬화한 문자열이 아니라 원본 data 를 그대로 돌려줘야 한다.
            // 재직렬화만 해도(예: 리터럴 nbsp 문자가 &nbsp; 엔티티로 바뀌는 등) 내용은 같아도
            // 문자열이 달라져서, 호출한 쪽의 "진전 없음"(start === data) 판정이 한 바퀴
            // 헛돈다(실측 확인).
            const consumed = buffer.childNodes.length > 0;
            remain = consumed === false ? data : dom.innerHTML === '' ? true : dom.innerHTML;

            if (consumed === true) {
                // buffer 에 모인 건 이미 CopyPaste.onPaste() 가 태그/속성 화이트리스트로
                // 정리해준 것 그대로다(재정리할 필요 없음) — 다만 우리가 직접 끼워넣은 <br> 은
                // 그 중 상당수가 실제로는 안쪽/바깥쪽이 겹쳐 필요 없어진 경우가 있으므로,
                // 연속된 <br> 은 하나로 합치고 맨 앞/뒤에 낀 건 지운다.
                html = buffer.innerHTML
                    .replace(/(<br>\s*){2,}/g, '<br>')
                    .replace(/^(<br>\s*)/, '')
                    .replace(/(<br>\s*)$/, '');
            }
        }

        if (html !== '') {
            if (this.getEditor().focus.getElement() === this) {
                this.#insertAtCaret(html);
            } else {
                await this.setValue(html);
            }
        }

        return { remain, matched };
    }

    /**
     * 클립보드에서 붙여넣기 하였을 때, "덩어리째로 최대한 끌어모으는" `onPaste()`와 달리 맨
     * 처음 만나는 텍스트노드 딱 하나만 뽑아 붙여넣는다 — 콜아웃 제목처럼 블록 전체가 아니라
     * 블록의 일부(짧은 필드 하나)만 차지하는 텍스트 엘리먼트용이다. 큰 덩어리를 붙여넣어도
     * 이 필드엔 맨 앞의 텍스트노드 하나만 들어가고, 그 이후(인라인 서식이든 더 이어지는
     * 텍스트든)는 절대 건드리지 않은 채 그대로 남겨 다음 블록타입(본문 등)에게 넘긴다.
     *
     * `wrappers`를 펼치며 내려가다 텍스트노드를 만나면 그걸로 확정하고, 그 전에 wrapper 가
     * 아닌 엘리먼트(인라인 서식 포함)를 먼저 만나면 그 자리에서 포기한다(전혀 못 찾음) — 이
     * 필드는 그런 경우까지 스스로 판단해서 받을 만큼 똑똑할 필요가 없고, 그런 데이터는
     * 통째로 다음 블록타입에게 넘기는 편이 낫다.
     *
     * @param type - 클립보드 타입
     * @param data - 클립보드 데이터
     * @param wrappers - 항상 투명하게 펼쳐볼 구조적 wrapper 태그/셀렉터 목록
     * @returns `remain`(처리결과, true 면 모든 데이터를 처리함, 그렇지 않으면 처리되지 않은 데이터)
     */
    async onPasteFirstText(type: string, data: any, wrappers: string[]): Promise<{ remain: true | string }> {
        if (type.startsWith('text/') === false) {
            return { remain: data };
        }

        if (type === 'text/plain') {
            const html = Html.escape(data as string).replace(/\r\n|\r|\n/g, '<br>');
            if (this.getEditor().focus.getElement() === this) {
                this.#insertAtCaret(html);
            } else {
                await this.setValue(html);
            }
            return { remain: true };
        }

        const dom = document.createElement('div');
        dom.innerHTML = data as string;

        const isWrapper = (el: globalThis.Element): boolean =>
            wrappers.some((wrapper) => {
                const match = wrapper.match(/^([a-z-]+)\[data-type='([^']*)'\]$/i);
                if (match !== null) {
                    return el.tagName === match[1].toUpperCase() && el.getAttribute('data-type') === match[2];
                }
                return el.tagName === wrapper;
            });

        // 각 레벨에서 첫 번째(코멘트 제외) 자식이 전부를 결정한다 — 텍스트노드면 그걸로 확정,
        // wrapper 면 그 안으로 재귀해 나온 결과를 그대로 돌려주고(그 결과가 null 이어도 다른
        // 형제를 더 찾지 않는다 — 문서 순서상 그 wrapper 안에서 이미 막혔다면 그 뒤 형제는
        // 순서상 더 뒤이므로 볼 이유가 없다), 그 외(wrapper 도 텍스트도 아님)면 즉시 포기.
        const findFirstText = (el: globalThis.Element): globalThis.Text | null => {
            for (const child of Array.from(el.childNodes)) {
                if (child.nodeType === Node.COMMENT_NODE) {
                    continue;
                }
                if (child.nodeType === Node.TEXT_NODE) {
                    return child as unknown as globalThis.Text;
                }
                if (child.nodeType === Node.ELEMENT_NODE && isWrapper(child as globalThis.Element) === true) {
                    return findFirstText(child as globalThis.Element);
                }
                return null;
            }
            return null;
        };

        const found = findFirstText(dom);
        if (found === null) {
            return { remain: data };
        }

        const html = Html.escape(found.textContent ?? '');
        found.remove();

        if (html !== '') {
            if (this.getEditor().focus.getElement() === this) {
                this.#insertAtCaret(html);
            } else {
                await this.setValue(html);
            }
        }

        return { remain: dom.innerHTML === '' ? true : dom.innerHTML };
    }

    /**
     * 추출한 html 을 지금 캐럿(포커스를 가진 나 자신 안)에 그대로 삽입한다 — 붙여넣기 직전에
     * 선택영역은 이미 지워져 캐럿이 collapse 된 상태라고 가정한다(`CopyPaste.#deleteSelection()`
     * 참고).
     *
     * @param html - 삽입할(이미 허용 태그/색상만 남도록 정리된) HTML 문자열
     */
    #insertAtCaret(html: string): void {
        const range = this.getEditor().selection.getRange();
        if (range === null) {
            return;
        }

        this.onBeforeEdit();

        const fragment = document.createRange().createContextualFragment(html);
        const lastNode = fragment.lastChild;

        range.deleteContents();
        range.insertNode(fragment);

        if (lastNode !== null) {
            const after = document.createRange();
            after.setStartAfter(lastNode);
            after.collapse(true);
            this.getEditor().selection.setRange(after);
        }

        this.#syncEmpty();
        this.onEdit();
    }

    /**
     * 이 엘리먼트에 곧 (네이티브) 편집이 일어날 것을 예고한다. 마우스 드래그로 선택한 뒤
     * 편집하면 이 엘리먼트가 아니라 조상 컨테이너로 `document.activeElement`가 가 있어서
     * (실측 확인) beforeinput/input이 엉뚱한 target으로 뜨고 이 엘리먼트 자신의
     * `input` 리스너가 못 타는 경우가 있다 — 호출한 쪽에서 한 프레임 뒤 `checkSynced()`로
     * 실제로 탔는지 확인해 안 탔으면 직접 동기화하도록 대비한다.
     */
    expectEdit(): void {
        this.#synced = false;
    }

    /**
     * `expectEdit()` 예고 이후 실제로 이 엘리먼트 자신의 `input`/`compositionend` 리스너가
     * 탔는지 확인한다. 탔으면 아무 일도 하지 않고(중복 실행 방지), 못 탔으면 대신 직접
     * 동기화(빈 상태 반영 + 캐럿 복구 + 히스토리 기록)한다.
     */
    checkSynced(): void {
        if (this.#synced === true) {
            return;
        }

        this.#synced = true;
        this.#syncEmpty();
        this.onEdit();
    }

    /**
     * 텍스트 입력이 될때마다 엘리먼트가 비었는지 여부를 동기화한다.
     */
    #syncEmpty(): void {
        const $el = this.$getElement();
        if (this.isEmpty() === false) {
            $el.removeAttr('data-empty');
            return;
        }

        if ($el.toHtml(true) === '') {
            $el.html('<br>');
        }

        $el.setAttr('data-empty', 'true');

        // 전체선택 후 삭제 등으로 이 엘리먼트가 방금 비워졌는데 캐럿이 계속 여기 있었다면
        // (blur→focus 왕복이 없어 화면에 캐럿이 보이지 않게 되는 문제, 실측 확인) — 캐럿을
        // 엘리먼트 맨 앞으로 다시 명시적으로 옮겨 렌더링을 되살린다.
        if (document.activeElement === $el.getEl()) {
            this.focus(true);
        }
    }
}
